import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingCart, Truck, Shield, RotateCcw, ChevronRight, Star, Zap, CheckCircle, Package, X, ChevronLeft, Send, Clock, Eye, Bell, HandCoins, LogIn, TrendingDown, Sun, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PromoBanner from "../components/PromoBanner";
import { toast } from "sonner";
import { resolveImageUrl, getImageFallback } from "../utils/imageUrl";
import RecentlyViewed from "../components/RecentlyViewed";
import { ProductAccessories } from "../components/Accessories";
import SunSpinner from "../components/SunSpinner";
import { useProductDetail } from "../hooks/useProductDetail";
import { ProductGallery, ProductLightbox } from "../components/product/ProductGallery";
import { ProductTabs, ProductShareStrip } from "../components/product/ProductTabs";

/* ── Shared helpers ── */

const STOCK_LABELS = {
    "in_stock": { label: "Skladem ihned", color: "text-green-600 bg-green-50 border-green-200" },
    "out_of_stock": { label: "Vyprodano", color: "text-red-600 bg-red-50 border-red-200" },
    "backorder": { label: "Na objednavku", color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
};

function getStockBadge(qty, status, thresholds, isConfigurator) {
    if (isConfigurator) return { text: "Na konfiguraci", color: "bg-blue-500 text-white" };
    if (status === "out_of_stock" || qty <= 0) return { text: "Vyprodano", color: "bg-red-500 text-white" };
    if (status === "backorder") return { text: "Na objednavku", color: "bg-amber-500 text-white" };
    const sorted = [...thresholds].sort((a, b) => b.min - a.min);
    for (const t of sorted) {
        if (qty >= t.min) return { text: `${t.label} (${qty} ks)`, color: "bg-green-600 text-white" };
    }
    return { text: `Posledni (${qty} ks)`, color: "bg-amber-500 text-white" };
}

function StarRating({ rating, size = 20 }) {
    return (
        <div className="flex gap-0.5">
            {[1,2,3,4,5].map(n => (
                <Star key={n} size={size}
                    className={rating >= n ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
            ))}
        </div>
    );
}

function isSaleActive(product) {
    if (!product?.sale_price || product.sale_price >= product.price) return false;
    const now = new Date();
    if (product.sale_start && new Date(product.sale_start) > now) return false;
    if (product.sale_end && new Date(product.sale_end) < now) return false;
    return true;
}

function useCountdown(endDate) {
    const [timeLeft, setTimeLeft] = React.useState(null);
    React.useEffect(() => {
        if (!endDate) return;
        const end = new Date(endDate).getTime();
        const tick = () => {
            const diff = end - Date.now();
            if (diff <= 0) { setTimeLeft(null); return; }
            setTimeLeft({
                d: Math.floor(diff / 86400000),
                h: Math.floor((diff % 86400000) / 3600000),
                m: Math.floor((diff % 3600000) / 60000),
                s: Math.floor((diff % 60000) / 1000),
            });
        };
        tick();
        const iv = setInterval(tick, 1000);
        return () => clearInterval(iv);
    }, [endDate]);
    return timeLeft;
}

/* ── Main component ── */

export default function ProductDetailPage() {
    const h = useProductDetail();
    const { product, loading, slug, navigate, qty, setQty, activeImg, setActiveImg,
        lightbox, setLightbox, selectedVariant, setSelectedVariant,
        watchSubmitted, watchLoading, watchType, setWatchType, targetPrice, setTargetPrice,
        customer, reviews, reviewsAvg, reviewsTotal,
        addingReview, setAddingReview, reviewForm, setReviewForm, submittingReview,
        stickyVisible, brandLogo, categoryPath, addToCartRef,
        betterPriceOpen, setBetterPriceOpen, betterPriceForm, setBetterPriceForm,
        betterPriceLoading, betterPriceSent,
        showConfigurator, setShowConfigurator, brandDescription,
        customerGroupPrice, showBuyNow, faqLd, abMeta, stockThresholds, activeTab, setActiveTab,
        related, handleAddToCart, handleWatchdog, handleBuyNow,
        handleSubmitReview, handleBetterPrice,
    } = h;

    const saleEndDate = product?.sale_end && isSaleActive(product) ? product.sale_end : null;
    const countdown = useCountdown(saleEndDate);

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center py-24" data-testid="product-page-loader">
                    <SunSpinner size="lg" text="Nacitam produkt..." />
                </div>
            </div>
        );
    }
    if (!product) return null;

    /* ── Computed values ── */
    const images = [product.main_image, ...(product.images || [])].filter(Boolean).map(resolveImageUrl);
    if (images.length === 0) images.push("");
    const saleActive = isSaleActive(product);
    const variantPrice = selectedVariant?.price || null;
    const variantSalePrice = selectedVariant?.sale_price || null;
    const effectivePrice = variantPrice || product.price;
    const effectiveSalePrice = variantSalePrice || product.sale_price;
    const finalPrice = saleActive && effectiveSalePrice && effectiveSalePrice < effectivePrice ? effectiveSalePrice : effectivePrice;
    const hasDiscount = saleActive && effectiveSalePrice && effectiveSalePrice < effectivePrice;
    const isConfigurator = product.product_type === "configurator";
    const configuratorUrl = product.configurator_url || "https://poptavka.solar-green.cz/";
    const displayStockStatus = isConfigurator ? "in_stock" : product.stock_status;
    const discountPct = hasDiscount ? Math.round((1 - finalPrice / effectivePrice) * 100) : 0;

    /* ── SEO ── */
    const seoTitle = product.seo_title || `${product.name}${product.brand ? ` | ${product.brand}` : ""} | ${finalPrice.toLocaleString("cs-CZ")} Kc | Solar-Green.cz`;
    const seoDesc = product.seo_description || `${product.name}${product.brand ? ` od ${product.brand}` : ""}. Cena ${finalPrice.toLocaleString("cs-CZ")} Kc. Solar-Green.cz`;
    const seoKeywords = [product.name, product.brand, product.sku, product.category_name, "solar", "fotovoltaika"].filter(Boolean).join(", ");
    const productUrl = `https://solar-green.cz/produkt/${product.slug || slug}`;
    const ldJson = JSON.stringify({
        "@context": "https://schema.org", "@type": "Product",
        "name": product.name || "", "description": product.short_description || product.seo_description || "",
        "sku": product.sku || "", "gtin13": product.ean || undefined,
        "mpn": product.manufacturer_pn || product.sku || undefined,
        "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
        "image": images.length > 0 ? images : undefined, "url": productUrl,
        "offers": {
            "@type": "Offer", "url": productUrl, "price": String(finalPrice), "priceCurrency": "CZK",
            "availability": product.stock_status === "in_stock" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "itemCondition": "https://schema.org/NewCondition",
            "seller": { "@type": "Organization", "name": "Solar-Green s.r.o." },
            ...(hasDiscount ? { "priceValidUntil": new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0] } : {})
        },
        ...(reviewsTotal > 0 ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": reviewsAvg, "reviewCount": reviewsTotal, "bestRating": "5", "worstRating": "1" } } : {}),
    });
    const breadcrumbLd = JSON.stringify({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Solar-Green.cz", "item": "https://solar-green.cz" },
            ...(categoryPath || []).map((c, i) => ({ "@type": "ListItem", "position": i + 2, "name": c.name, "item": `https://solar-green.cz/kategorie/${c.slug}` })),
            { "@type": "ListItem", "position": (categoryPath || []).length + 2, "name": product.name }
        ]
    });
    const effectiveTitle = abMeta?.title || seoTitle || "Produkt | Solar-Green.cz";
    const effectiveDesc = abMeta?.description || seoDesc;

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>{effectiveTitle}</title>
                <meta name="description" content={effectiveDesc} />
                <meta name="keywords" content={seoKeywords} />
                <meta property="og:title" content={effectiveTitle} />
                <meta property="og:description" content={effectiveDesc} />
                <meta property="og:type" content="product" />
                <meta property="og:url" content={productUrl} />
                {images[0] && <meta property="og:image" content={images[0]} />}
                <meta property="product:price:amount" content={String(finalPrice)} />
                <meta property="product:price:currency" content="CZK" />
                <link rel="canonical" href={productUrl} />
                <script type="application/ld+json">{ldJson}</script>
                <script type="application/ld+json">{breadcrumbLd}</script>
                {faqLd && <script type="application/ld+json">{faqLd}</script>}
            </Helmet>
            <Navbar />

            {/* Breadcrumb */}
            <div className="bg-gray-50 border-b border-gray-100">
                <div className="sg-container py-3 flex items-center gap-2 text-sm text-gray-500">
                    <Link to="/" className="hover:text-green-700">Domu</Link>
                    {categoryPath.map((cat) => (
                        <React.Fragment key={cat.id}>
                            <ChevronRight size={12} />
                            <Link to={`/kategorie/${cat.slug || cat.id}`} className="hover:text-green-700">{cat.name}</Link>
                        </React.Fragment>
                    ))}
                    {categoryPath.length === 0 && product.category_name && (
                        <><ChevronRight size={12} /><span className="text-gray-800 font-medium">{product.category_name}</span></>
                    )}
                </div>
            </div>

            <div className="sg-container py-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Gallery */}
                    <ProductGallery product={product} images={images} activeImg={activeImg} setActiveImg={setActiveImg}
                        setLightbox={setLightbox} brandLogo={brandLogo} slug={slug} hasDiscount={hasDiscount} discountPct={discountPct} />

                    {/* Product info */}
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 leading-tight" data-testid="product-title">
                            {product.brand && !product.name.toLowerCase().startsWith(product.brand.toLowerCase()) && (
                                <span className="text-gray-500 font-semibold">{product.brand} </span>
                            )}
                            {product.name}
                        </h1>

                        {reviewsTotal > 0 && (
                            <div className="flex items-center gap-2 mb-3">
                                <StarRating rating={Math.round(reviewsAvg)} size={16} />
                                <span className="text-sm font-semibold text-gray-700">{reviewsAvg}</span>
                                <span className="text-sm text-gray-400">({reviewsTotal} recenzi)</span>
                            </div>
                        )}

                        {product.short_description && (
                            <p className="text-gray-600 leading-relaxed mb-4 text-sm" dangerouslySetInnerHTML={{ __html: product.short_description }} />
                        )}

                        {/* Variant selector */}
                        {product.variants && product.variants.length > 0 && (
                            <div className="mb-4 p-3 rounded-xl border border-gray-200 bg-gray-50/50" data-testid="variant-selector">
                                <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Vyberte variantu</p>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => setSelectedVariant(null)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${!selectedVariant ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                                        data-testid="variant-option-default">
                                        Zakladni
                                    </button>
                                    {product.variants.map((v, i) => (
                                        <button key={v.variant_name || i} onClick={() => setSelectedVariant(v)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${selectedVariant === v ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                                            data-testid={`variant-option-${i}`}>
                                            {v.variant_name}
                                            {v.price && v.price !== product.price && <span className="ml-1 text-gray-400">{v.price.toLocaleString("cs-CZ")} Kc</span>}
                                        </button>
                                    ))}
                                </div>
                                {selectedVariant && (
                                    <div className="mt-2 text-xs text-gray-500">
                                        {selectedVariant.sku && <span className="mr-3">SKU: {selectedVariant.sku}</span>}
                                        <span>Sklad: {selectedVariant.stock_quantity > 0 ? `${selectedVariant.stock_quantity} ks` : "Na dotaz"}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Info boxes */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4" data-testid="product-info-boxes">
                            {product.sku && (
                                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                                    <div className="flex items-start gap-2">
                                        <span className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">#</span>
                                        <div className="min-w-0">
                                            <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wider leading-tight">SKU</p>
                                            <p className="text-xs font-bold text-gray-800 truncate">{product.sku}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {product.manufacturer_pn && (
                                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                                    <div className="flex items-start gap-2">
                                        <span className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">PN</span>
                                        <div className="min-w-0">
                                            <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wider leading-tight">PN vyrobce</p>
                                            <p className="text-xs font-bold text-gray-800 truncate">{product.manufacturer_pn}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {product.ean && (
                                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                                    <div className="flex items-start gap-2">
                                        <span className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">EAN</span>
                                        <div className="min-w-0">
                                            <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wider leading-tight">EAN</p>
                                            <p className="text-xs font-bold text-gray-800 truncate">{product.ean}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                                <div className="flex items-start gap-2">
                                    <span className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center text-white flex-shrink-0"><CheckCircle size={13} /></span>
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wider leading-tight">Zaruka</p>
                                        <span className="inline-block bg-green-600 text-white text-[11px] font-bold px-2 py-0.5 rounded mt-0.5">
                                            {(() => {
                                                const w = product.warranty_months;
                                                const months = (!w || w <= 0) ? 24 : w;
                                                if (months >= 24 && months % 12 === 0) return `${months / 12} let`;
                                                if (months >= 12) return `${Math.floor(months / 12)} r. ${months % 12 > 0 ? `${months % 12} mes.` : ""}`.trim();
                                                return `${months} mesicu`;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                                <div className="flex items-start gap-2">
                                    <span className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">SK</span>
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase font-semibold tracking-wider leading-tight">Sklad</p>
                                        {(() => {
                                            const badge = getStockBadge(product.stock_quantity || 0, displayStockStatus, stockThresholds, isConfigurator);
                                            return <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded mt-0.5 ${badge.color}`} data-testid="stock-badge">{badge.text}</span>;
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Price */}
                        <div className={`rounded-lg p-4 mb-4 ${saleActive ? "bg-red-50 border border-red-100" : "bg-gray-50 border border-gray-100"}`} data-testid="product-price-box">
                            {saleActive && countdown && (
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-red-100/60 text-xs" data-testid="sale-countdown">
                                    <Clock size={13} className="text-orange-500" />
                                    <span className="font-semibold text-orange-700">Akce konci za</span>
                                    <div className="flex gap-1 ml-auto">
                                        {countdown.d > 0 && <span className="bg-orange-600 text-white font-mono font-bold rounded px-1.5 py-0.5 text-xs">{countdown.d}d</span>}
                                        <span className="bg-orange-600 text-white font-mono font-bold rounded px-1.5 py-0.5 text-xs">{String(countdown.h).padStart(2, "0")}h</span>
                                        <span className="bg-orange-600 text-white font-mono font-bold rounded px-1.5 py-0.5 text-xs">{String(countdown.m).padStart(2, "0")}m</span>
                                        <span className="bg-orange-600 text-white font-mono font-bold rounded px-1.5 py-0.5 text-xs">{String(countdown.s).padStart(2, "0")}s</span>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-end gap-2 mb-0.5">
                                <span className={`text-2xl font-bold ${hasDiscount ? "text-red-600" : "text-gray-900"}`} data-testid="product-price">
                                    {finalPrice.toLocaleString("cs-CZ")} Kc
                                </span>
                                {hasDiscount && <span className="text-sm text-gray-400 line-through mb-0.5">{product.price.toLocaleString("cs-CZ")} Kc</span>}
                                {hasDiscount && <span className="text-xs font-bold text-white bg-red-500 px-2 py-0.5 rounded mb-0.5">-{discountPct}%</span>}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Bez DPH: {(finalPrice / (1 + (product.tax_rate || 21) / 100)).toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kc
                                <span className="text-gray-400 ml-1">(DPH {product.tax_rate || 21}%)</span>
                            </p>
                            {hasDiscount && (
                                <p className="text-xs text-green-600 mt-0.5 font-medium">Usetrite {(product.price - finalPrice).toLocaleString("cs-CZ")} Kc</p>
                            )}
                            {customerGroupPrice && (
                                <div className="mt-2 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200" data-testid="b2b-group-price">
                                    <p className="text-xs text-blue-700">B2B: <strong>{customerGroupPrice.price.toLocaleString("cs-CZ")} Kc</strong> <span className="text-blue-500 ml-1">(-{customerGroupPrice.discount}%)</span></p>
                                </div>
                            )}
                        </div>

                        {/* Volume Pricing */}
                        {product.volume_pricing && product.volume_pricing.length > 1 && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3 mb-4" data-testid="volume-pricing">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <Package size={13} className="text-emerald-600" />
                                    <span className="text-xs font-bold text-emerald-800">Mnozstevni slevy</span>
                                </div>
                                <div className="grid gap-1">
                                    {[...product.volume_pricing].sort((a, b) => a.min_qty - b.min_qty).map((tier, i, arr) => {
                                        const isActive = qty >= tier.min_qty && (i === arr.length - 1 || qty < arr[i + 1].min_qty);
                                        const basePrice = arr[0]?.price || product.price;
                                        const savePct = basePrice > 0 ? Math.round((1 - tier.price / basePrice) * 100) : 0;
                                        return (
                                            <div key={tier.min_qty} className={`flex items-center justify-between px-2.5 py-1.5 rounded text-xs transition-colors ${isActive ? "bg-emerald-600 text-white" : "bg-white border border-gray-100"}`}>
                                                <span className="font-medium">{tier.min_qty}+ ks</span>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-bold">{tier.price.toLocaleString("cs-CZ")} Kc/ks</span>
                                                    {savePct > 0 && <span className={`font-bold text-[10px] px-1 py-0.5 rounded ${isActive ? "bg-white/20" : "bg-emerald-100 text-emerald-700"}`}>-{savePct}%</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Unified Promo Banner */}
                        <PromoBanner product={product} categoryPath={categoryPath} />

                        {/* Cart / Configurator */}
                        <div ref={addToCartRef}>
                            {isConfigurator ? (
                                <div className="space-y-3 mb-3">
                                    {!showConfigurator ? (
                                        <Button onClick={() => setShowConfigurator(true)} data-testid="product-configure-btn"
                                            className="w-full text-white h-11 rounded-lg font-semibold gap-2"
                                            style={{ backgroundColor: "var(--sg-primary)" }}>
                                            <Zap size={16} /> Konfigurovat
                                        </Button>
                                    ) : (
                                        <div className="rounded-xl border border-gray-200 overflow-hidden shadow-lg" data-testid="product-configurator-wrapper">
                                            <div className="bg-gray-900 px-4 py-2 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Zap size={14} className="text-lime-400" />
                                                    <span className="text-sm font-medium text-white">Konfigurator</span>
                                                </div>
                                                <button onClick={() => setShowConfigurator(false)} className="text-gray-400 hover:text-white transition-colors p-1" data-testid="close-product-configurator">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                            <iframe src={configuratorUrl} title="Konfigurator" className="w-full border-0"
                                                style={{ height: "70vh", minHeight: "500px" }}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                                                data-testid="product-configurator-iframe" />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                        <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={product.stock_status === "out_of_stock"} className="w-10 h-11 flex items-center justify-center hover:bg-gray-50 text-gray-600 font-semibold disabled:opacity-40">-</button>
                                        <span className="w-12 text-center font-medium text-gray-800" data-testid="product-qty">{qty}</span>
                                        <button onClick={() => setQty(q => q + 1)} disabled={product.stock_status === "out_of_stock"} className="w-10 h-11 flex items-center justify-center hover:bg-gray-50 text-gray-600 font-semibold disabled:opacity-40">+</button>
                                    </div>
                                    <Button onClick={handleAddToCart} data-testid="product-add-to-cart"
                                        disabled={product.stock_status === "out_of_stock"}
                                        className="flex-1 text-white h-11 rounded-lg font-medium disabled:opacity-50"
                                        style={{ backgroundColor: "var(--sg-primary)" }}>
                                        <ShoppingCart size={16} className="mr-2" />
                                        {product.stock_status === "out_of_stock" ? "Vyprodano" : "Pridat do kosiku"}
                                    </Button>
                                </div>
                                {showBuyNow && (
                                <Button variant="outline" onClick={product.stock_status !== "out_of_stock" ? handleBuyNow : undefined}
                                    data-testid="product-buy-now"
                                    className="w-full h-11 rounded-lg font-medium mb-3"
                                    style={{ borderColor: "var(--sg-primary)", color: "var(--sg-primary-dark)" }}
                                    disabled={product.stock_status === "out_of_stock"}>
                                    {product.stock_status === "out_of_stock" ? "Vyprodano" : "Koupit ihned"}
                                </Button>
                                )}
                                </>
                            )}

                            {/* Action strip: Watchdog / Price Watch / Better Price */}
                            {!isConfigurator && (
                            <div className="flex flex-wrap gap-2 mb-4" data-testid="product-action-strip">
                                {product.stock_status === "out_of_stock" && (
                                    watchSubmitted && watchType === "stock" ? (
                                        <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-testid="watchdog-section">
                                            <CheckCircle size={14} /> Hlidame dostupnost
                                        </div>
                                    ) : !customer ? (
                                        <Link to="/prihlaseni" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors" data-testid="watchdog-login-link">
                                            <Eye size={14} /> Hlidat sklad <LogIn size={11} className="ml-0.5 opacity-60" />
                                        </Link>
                                    ) : (
                                        <button onClick={() => { setWatchType("stock"); setTimeout(handleWatchdog, 10); }} disabled={watchLoading}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
                                            data-testid="watchdog-subscribe-btn">
                                            <Bell size={14} /> {watchLoading ? "..." : "Hlidat sklad"}
                                        </button>
                                    )
                                )}
                                {watchSubmitted && watchType === "price" ? (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-testid="price-watch-section">
                                        <CheckCircle size={14} /> Hlidame cenu
                                    </div>
                                ) : !customer ? (
                                    <Link to="/prihlaseni" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors" data-testid="price-watch-login-link">
                                        <TrendingDown size={14} /> Hlidat cenu <LogIn size={11} className="ml-0.5 opacity-60" />
                                    </Link>
                                ) : (
                                    <div className="inline-flex items-center gap-1.5" data-testid="price-watch-section">
                                        <div className="flex items-center rounded-lg overflow-hidden border border-blue-200">
                                            <Input type="number" value={targetPrice} onChange={e => setTargetPrice(e.target.value)}
                                                placeholder="Cil. cena" className="h-8 w-24 text-xs bg-blue-50 border-0 rounded-none focus-visible:ring-0" data-testid="price-watch-target-input" />
                                            <button onClick={() => { setWatchType("price"); setTimeout(handleWatchdog, 10); }} disabled={watchLoading}
                                                className="h-8 px-3 bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                                data-testid="price-watch-btn">
                                                <TrendingDown size={12} /> Hlidat
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {betterPriceSent ? (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-testid="better-price-section">
                                        <CheckCircle size={14} /> Nabidka odeslana
                                    </div>
                                ) : betterPriceOpen ? (
                                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="better-price-section">
                                        <div className="flex items-center rounded-lg overflow-hidden border border-emerald-200">
                                            <Input type="number" value={betterPriceForm.offered_price}
                                                onChange={e => setBetterPriceForm(f => ({...f, offered_price: e.target.value}))}
                                                placeholder="Vase cena" className="h-8 w-24 text-xs bg-emerald-50 border-0 rounded-none focus-visible:ring-0" data-testid="better-price-input" />
                                            <button onClick={handleBetterPrice} disabled={betterPriceLoading}
                                                className="h-8 px-3 bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                                data-testid="better-price-submit">
                                                <Send size={11} /> {betterPriceLoading ? "..." : "Odeslat"}
                                            </button>
                                        </div>
                                        <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setBetterPriceOpen(false)}>X</button>
                                    </div>
                                ) : (
                                    <button onClick={() => {
                                        if (!customer) { toast.error("Pro odeslani nabidky se prosim prihlaste"); navigate("/prihlaseni"); return; }
                                        setBetterPriceOpen(true);
                                    }}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors"
                                        data-testid="better-price-toggle">
                                        <HandCoins size={14} /> Chci lepsi cenu
                                    </button>
                                )}
                            </div>
                            )}

                            {/* FVE Konfigurator */}
                            {categoryPath.some(c => c.slug === "fotovoltaika") && (
                            <Link to="/konfigurator"
                                className="flex items-center gap-2.5 mb-3 px-3 py-2.5 rounded-lg bg-green-50 border border-green-200 hover:border-green-300 hover:shadow-sm transition-all group"
                                data-testid="product-fve-link">
                                <Sun size={16} className="text-green-600 flex-shrink-0" />
                                <span className="text-xs font-bold text-green-700 group-hover:text-green-800">FVE Konfigurator</span>
                                <ArrowRight size={14} className="text-green-400 group-hover:text-green-600 ml-auto" />
                            </Link>
                            )}

                            {/* Print / Share */}
                            <ProductShareStrip product={product} />
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <ProductTabs product={product} activeTab={activeTab} setActiveTab={setActiveTab}
                    brandDescription={brandDescription} brandLogo={brandLogo}
                    reviews={reviews} reviewsTotal={reviewsTotal} reviewsAvg={reviewsAvg}
                    addingReview={addingReview} setAddingReview={setAddingReview}
                    reviewForm={reviewForm} setReviewForm={setReviewForm}
                    submittingReview={submittingReview} handleSubmitReview={handleSubmitReview} />

                {/* Related */}
                {related.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Souvisejici produkty</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {related.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                )}

                {/* Accessories */}
                <ProductAccessories productId={product?.id} />
                <RecentlyViewed excludeId={product?.id} maxShow={6} />
            </div>

            {/* Trust badges */}
            <div className="bg-gray-50 border-t border-gray-200 mt-12" data-testid="trust-badges-section">
                <div className="sg-container py-8">
                    <div className="grid grid-cols-3 gap-6">
                        {[
                            { icon: Truck, label: "Doprava", desc: "PPL od 199 Kc" },
                            { icon: Shield, label: "Zaruka", desc: `${(() => { const w = product.warranty_months; const m = (!w || w <= 0) ? 24 : w; return m >= 24 && m % 12 === 0 ? `${m/12} let` : m >= 12 ? `${Math.floor(m/12)} r.` : `${m} mes.`; })()}` },
                            { icon: RotateCcw, label: "Vraceni", desc: "30 dnu" },
                        ].map(item => (
                            <div key={item.label} className="text-center">
                                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-sm border border-gray-100">
                                    <item.icon size={24} className="text-gray-500" />
                                </div>
                                <p className="text-sm font-bold text-gray-800">{item.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Footer />

            {/* Lightbox */}
            {lightbox && <ProductLightbox images={images} activeImg={activeImg} setActiveImg={setActiveImg} onClose={() => setLightbox(false)} />}

            {/* Sticky CTA bar */}
            {stickyVisible && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[500] px-4 py-3">
                    <div className="sg-container flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{product.name}</p>
                            <p className="text-lg font-bold" style={{ color: "var(--sg-primary-dark)" }}>{finalPrice.toLocaleString("cs-CZ")} Kc</p>
                        </div>
                        {isConfigurator ? (
                            <Button onClick={() => { setShowConfigurator(true); document.querySelector('[data-testid="product-configure-btn"]')?.scrollIntoView({ behavior: "smooth" }); }}
                                className="text-white font-semibold px-6 h-10 flex-shrink-0 gap-2"
                                style={{ backgroundColor: "var(--sg-primary)" }} data-testid="sticky-configure-btn">
                                <Zap size={15} /> Konfigurovat
                            </Button>
                        ) : (
                            <>
                                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                    <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={product.stock_status === "out_of_stock"} className="w-8 h-9 flex items-center justify-center hover:bg-gray-50 text-gray-600 disabled:opacity-40">-</button>
                                    <span className="w-10 text-center text-sm font-medium">{qty}</span>
                                    <button onClick={() => setQty(q => q + 1)} disabled={product.stock_status === "out_of_stock"} className="w-8 h-9 flex items-center justify-center hover:bg-gray-50 text-gray-600 disabled:opacity-40">+</button>
                                </div>
                                <Button onClick={handleAddToCart} disabled={product.stock_status === "out_of_stock"}
                                    className="text-white font-semibold px-6 h-10 flex-shrink-0 disabled:opacity-50"
                                    style={{ backgroundColor: "var(--sg-primary)" }} data-testid="sticky-add-to-cart">
                                    <ShoppingCart size={15} className="mr-1.5" /> {product.stock_status === "out_of_stock" ? "Vyprodano" : "Do kosiku"}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
